<template>
  <div class="DropdownItem">
    <div class="DropdownItem-content">
      <Icon
        v-if="leftIcon"
        class="DropdownItem-leftIcon"
        :icon="leftIcon"
        size="md"
      />

      <span class="DropdownItem-text">
        {{ text }}
      </span>

      <Icon
        v-if="rightIcon"
        class="DropdownItem-rightIcon"
        :icon="rightIcon"
        size="md"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from '../../atoms/Icon/Icon.vue';
import type { IconName } from '../../atoms/Icon/Icon.model';

export interface DropdownItemProps {
  text?: string;
  leftIcon?: IconName;
  rightIcon?: IconName;
}

defineProps<DropdownItemProps>();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';

@layer cvo-ui {
  .DropdownItem {
    position: relative;
    min-height: 29px;
    padding: vars.$atoms-dropdown-baseitem-padding-top-bottom vars.$atoms-dropdown-baseitem-padding-left-right;

    cursor: pointer;
    background-color: vars.$atoms-dropdown-baseitem-default-bg-color;
    border-radius: vars.$atoms-dropdown-baseitem-border-radius;
    transition: background-color 0.2s ease;
    // border-block-end: 1px solid vars.$border-primary; // Removed to match token based design usually devoid of separators if not specified, complying with baseitem styles

    &:focus-visible {
      outline: none;
    }

    color: vars.$atoms-dropdown-baseitem-default-label-color;
    .DropdownItem-leftIcon, .DropdownItem-rightIcon {
        color: vars.$atoms-dropdown-baseitem-default-icon-color;
    }

    &[data-highlighted] {
      background-color: vars.$atoms-dropdown-baseitem-hover-bg-color;
      color: vars.$atoms-dropdown-baseitem-hover-label-color;

      .DropdownItem-leftIcon,
      .DropdownItem-rightIcon {
        color: vars.$atoms-dropdown-baseitem-hover-icon-color;
      }
    }

    &[data-state=checked] {
      background-color: vars.$atoms-dropdown-baseitem-active-bg-color;
      color: vars.$atoms-dropdown-baseitem-active-label-color;

      .DropdownItem-leftIcon,
      .DropdownItem-rightIcon {
         color: vars.$atoms-dropdown-baseitem-active-icon-color;
      }
    }

    &[data-disabled] {
      cursor: not-allowed;
      background-color: vars.$atoms-dropdown-baseitem-disabled-bg-color;
      color: vars.$atoms-dropdown-baseitem-disabled-label-color;

      .DropdownItem-leftIcon,
      .DropdownItem-rightIcon {
        color: vars.$atoms-dropdown-baseitem-disabled-icon-color;
      }
    }

    &[data-disabled][data-state=checked] {
      background-color: vars.$atoms-dropdown-baseitem-disabled-selected-bg-color;
      color: vars.$atoms-dropdown-baseitem-disabled-selected-label-color;

      .DropdownItem-leftIcon,
      .DropdownItem-rightIcon {
        color: vars.$atoms-dropdown-baseitem-disabled-selected-icon-color;
      }
    }

    &-content {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
      width: 100%;
    }

    &-leftIcon,
    &-rightIcon {
      @include layout.flex-center;
      flex-shrink: 0;
    }

    &-text {
      flex: 1;
      font-size: vars.$font-size-sm;
      font-weight: vars.$typography-weight-body;
      line-height: 1;
      letter-spacing: -0.42px;
      min-width: 0;
    }
  }
}
</style>
